import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Pagination } from "@/components/ui/pagination";
import { Plus, Edit2, Trash2, Shield, Lock, RefreshCw } from "lucide-react";
import { rolesApi, type Role } from "@/lib/api/roles";
import { cn } from "@/lib/utils";

const RolesSettingsPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { isTenantAdmin } = useAccessControl();
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<Role | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (isTenantAdmin) {
      loadRoles();
    }
  }, [isTenantAdmin]);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const data = await rolesApi.getAll();
      setRoles(data);
    } catch (error) {
      console.error("Failed to load roles:", error);
      toast.error("Unable to load roles. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (role: Role) => {
    navigate(`/setup/roles/${role.id}/edit`);
  };

  const openDeleteDialog = (role: Role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };

  const handleRefreshDefaultRoles = async () => {
    try {
      setRefreshing(true);
      await rolesApi.refreshDefaultRoles();
      toast.success(
        "Default roles updated. Please log out and log back in to refresh your access."
      );
      await loadRoles();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to refresh roles. Please try again.");
    } finally {
      setRefreshing(false);
    }
  };

  const handleDelete = async () => {
    if (!roleToDelete) return;

    try {
      setDeleting(true);
      await rolesApi.delete(roleToDelete.id);
      toast.success(`"${roleToDelete.name}" has been deleted.`);
      await loadRoles();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to delete role. Please try again.");
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setRoleToDelete(null);
    }
  };

  const filteredRoles = useMemo(() => {
    return roles.filter(
      (role) =>
        role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        role.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [roles, searchQuery]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Paginate the filtered results
  const paginatedRoles = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredRoles.slice(startIndex, endIndex);
  }, [filteredRoles, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredRoles.length / itemsPerPage);

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">You need administrator permissions to manage roles.</p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Roles & Permissions"
          description="Manage roles and their permissions for staff members"
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Roles" }]}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search roles..."
        />

        <div className="flex justify-end gap-2 mb-6">
          <Button
            variant="outline"
            onClick={handleRefreshDefaultRoles}
            disabled={refreshing}
            title="Update Tenant Admin, Librarian, etc. with latest permissions (e.g. Manage Invoices)"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh Default Roles
          </Button>
          <Button onClick={() => navigate("/setup/roles/new")} className="shadow-sm">
            <Plus className="w-4 h-4 mr-2" />
            Add Role
          </Button>
        </div>

        <Card className="overflow-hidden bg-white shadow-sm">
          {loading ? (
            <div className="py-16">
              <LoadingSpinner size="lg" label="Loading roles..." />
            </div>
          ) : filteredRoles.length === 0 ? (
            <div className="py-16 text-center">
              <div className="flex items-center justify-center w-16 h-16 p-4 mx-auto mb-4 bg-gray-100 rounded-full">
                <Shield className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {searchQuery ? "No matching roles" : "No roles yet"}
              </h3>
              <p className="max-w-sm mx-auto mb-6 text-sm text-gray-500">
                {searchQuery
                  ? "Try a different search term."
                  : "Create your first role to define permissions for staff members."}
              </p>
              {!searchQuery && (
                <Button onClick={() => navigate("/setup/roles/new")} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Role
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-100">
                {paginatedRoles.map((role) => (
                  <div key={role.id} className="p-5 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{role.name}</h3>
                          {role.isSystem && (
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary flex items-center gap-1">
                              <Lock className="w-3 h-3" />
                              System Role
                            </span>
                          )}
                        </div>
                        {role.description && (
                          <p className="mb-3 text-sm text-gray-500">{role.description}</p>
                        )}
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Shield className="w-4 h-4" />
                          <span>
                            {role.permissions.length} permission
                            {role.permissions.length !== 1 ? "s" : ""}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(role)}
                          className="gap-1.5"
                          disabled={role.isSystem}
                        >
                          <Edit2 className="w-4 h-4" />
                          <span className="hidden sm:inline">Edit</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDeleteDialog(role)}
                          className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
                          disabled={role.isSystem}
                        >
                          <Trash2 className="w-4 h-4" />
                          <span className="hidden sm:inline">Delete</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {filteredRoles.length > 0 && (
                <div className="px-5 py-4 border-t border-gray-200">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredRoles.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={setItemsPerPage}
                  />
                </div>
              )}
            </>
          )}
        </Card>
      </PageContainer>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={`Delete "${roleToDelete?.name}"?`}
        description={
          <>
            <p className="mb-2">
              This will <strong>permanently delete</strong> this role. This action cannot be undone.
            </p>
            <p className="font-medium text-amber-600">
              Make sure no staff members are assigned to this role.
            </p>
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        variant="danger"
        loading={deleting}
      />
    </AppLayout>
  );
};

export default RolesSettingsPage;
